<template>
  <div class="super-ops">
    <div class="wrapper">
      <div class="header">
        <i v-if="showChevron" class="fa fa-chevron-left" style="padding-right: 15px" @click="clear" />
        <span v-if="selected">
          <span @click="clear">{{ selected }}</span>

          <span class="status-filter" @click="toggleStatusFilter">
            <span v-if="statusFilter === 'all'" class="has-text-info">
              <i class="fa fa-circle" />&nbsp;All</span>
            <span v-else-if="statusFilter === 'ongoing'" class="has-text-primary">
              <i class="fa fa-circle-notch fa-spin" />&nbsp;Ongoing</span>
            <span v-else-if="statusFilter === 'problems'" class="has-text-danger">
              <i class="fa fa-exclamation-circle" />&nbsp;Problems</span>
            <span v-else class="has-text-warning">{{ statusFilter }}</span>
          </span>
          <span class="is-pulled-right">
            <i class="fa fa-sm" :class="fieldIcon" style="opacity: 0.8" @click="nextField" />
          </span>
        </span>
        <span v-else>
          <span @click="clear">Operations</span>
          <span class="is-pulled-right">
            <span v-if="isProduction()" class="has-text-info">Prod</span>
            <span v-else class="has-text-warning">Dev</span>
          </span>
        </span>
      </div>
      <div v-if="selected">
        <div v-if="updating" style="opacity: 0.3">
          <b-skeleton height="36px" />
          <b-skeleton height="36px" />
          <b-skeleton height="36px" />
        </div>
        <div v-else class="transactions-list no-scrollbar">
          <div v-if="!filteredTransactions.length" class="notification is-dark"
            style="background-color: #333; margin-bottom: -1px">
            None
          </div>
          <div v-for="t in filteredTransactions" :key="t.id" class="transaction no-scrollbar" :class="{
            deleted: t.is_deleted,
            'bg-test': isTest(t),
            'bg-pulse': pulseTransaction(t),
            'border-big': showAllFields,
          }" @click="moreInfo(t)">
            <p style="padding: 8px 4px">
              <span class="time" :class="{ 'has-text-danger': deliveryIssue(t) }">{{ shorterDateTime(t.created_at)
              }}</span>
              <span v-if="leftIcon(t)" class="left-icon">
                <i v-if="isTransport(t) && !t.driver" class="fa fa-car has-text-white" />
                <i v-if="isDelivery(t) && !t.driver" class="fa fa-motorcycle has-text-white" />
                <i v-else-if="isDinein(t)" class="fa fa-users" />
                <i v-else-if="isTakeaway(t)" class="fa fa-walking" />
              </span>
              <span class="status has-text-dark" :class="{ 'border-pulse': isCommerceDelayDelivery(t) }"
                :style="transactionStyle(t)">
                <span :class="{ 'has-text-weight-semibold': isFirstOrder(t) }">{{ shortState(t.state) }}</span>
                <span class="right-icons">
                  <i v-if="isCashback(t)" class="fa fa-money-bill-wave has-text-black" />
                  <i v-if="isLalamove(t)" class="fa fa-dove" />
                  <i v-if="t.data.gift_details" class="fa fa-gift" />
                  <i v-if="isAutoconfirm(t)" class="far fa-hourglass" />
                  <i v-if="isScheduled(t)" class="far fa-clock" />
                  <i v-if="t.data.event_cancelled_by_driver" class="far fa-window-close has-text-black" />
                  <i v-if="t.data.retry" class="fa fa-redo-alt has-text-black" />
                  <span v-if="t.data.retry_count > 1" class="retry-count">{{
                    t.data.retry_count
                  }}</span>
                  <i v-if="t.data.call_shop" class="fa fa-phone has-text-black" />
                </span>
              </span>

              <span :class="fieldColor" v-html="customerName(t)" />
              <span v-if="isTest(t)" class="is-test">TEST</span>
            </p>
            <div v-if="showAllFields">
              <p class="is-flex all-fields">
                <span class="has-text-info">
                  <!-- <i class="fa fa-store-alt fa-sm" />&nbsp; -->
                  {{ shortenString(fieldBusiness(t), 30) }}
                  <span v-if="t.data.ref">#{{ t.data.ref }}</span>
                </span>
                <span style="flex: 0">{{ fieldValue(t) }}</span>
              </p>
              <p class="is-flex all-fields">
                <span class="has-text-primary">{{ fieldDriverAgent(t) }}</span>
                <span style="flex: 0"><i class="far fa-clock fa-sm" />&nbsp;{{
                  timeAgo(t.updated_at)
                }}</span>
              </p>
            </div>
          </div>
          <div v-if="loadMore">
            <b-skeleton height="36px" />
          </div>
          <div v-else class="showing-toggle has-text-centered" @click="getMoreTransactions">
            <i class="fa fa-chevron-down" />
          </div>
        </div>

        <slide-y-down-transition>
          <div v-if="showSearch" class="search" key="1">
            <div v-if="matchingFields" class="search-fields tags">
              <span v-for="(v, k, i) in matchingFields" :key="i" class="tag is-medium is-dark">
                {{ k }}: {{ v }}
              </span>
            </div>
            <div class="field is-grouped">
              <p class="control is-expanded">
                <input ref="input" class="input is-medium" placeholder="duke, nick, confirmed" v-model="searchInput"
                  @keyup="search($event)" />
              </p>
              <p class="control">
                <a class="button is-dark is-medium" @click="clearSearch"><i class="fa fa-times-circle" /></a>
              </p>
            </div>
            <div>
              <div class="buttons is-flex" style="margin-bottom: 0">
                <span class="button" :class="{ 'is-black': statusFilter === 'problems' }"
                  @click="setStatusFilter('problems')"><i
                    class="fa fa-exclamation-circle has-text-danger" />&nbsp;Problems</span>
                <span class="button" :class="{ 'is-black': statusFilter === 'ongoing' }"
                  @click="setStatusFilter('ongoing')"><i class="fa fa-circle-notch fa-spin" />&nbsp;Ongoing</span>
                <span class="button" :class="{ 'is-black': statusFilter === 'all' }" @click="setStatusFilter('all')"><i
                    class="fa fa-circle" />&nbsp;All</span>
              </div>

              <div class="is-flex" style="margin-top: 4px; margin-bottom: 8px; gap: 3px">
                <span v-for="s in relevantStates" :key="s" class="button"
                  style="flex: 1; padding: 4px; font-size: 0.8em; line-height: 2em;"
                  :class="{ 'is-black': statusFilter === s }" @click="setStatusFilter(s)">{{
                    shortState(s)
                  }}</span>
              </div>
            </div>
          </div>
          <div v-else class="clear-btns" key="2">
            <p class="button is-medium is-rounded" @click="toggleSearch">
              <i class="fa fa-filter" />
            </p>
            <p class="button is-medium is-rounded" :class="{ 'is-black': selectedField === 'shop' }"
              @click="selectedField = 'shop'">
              <i class="fa fa-store-alt" />
            </p>
            <p class="button is-medium is-rounded" :class="{ 'is-black': selectedField === 'customer' }"
              @click="selectedField = 'customer'">
              <i class="far fa-user" />
            </p>
            <p class="button is-medium is-rounded" :class="{ 'is-black': selectedField === 'driver' }"
              @click="selectedField = 'driver'">
              <i class="fa fa-motorcycle" />
            </p>
            <p class="button is-medium is-rounded" :class="{ 'is-black': selectedField === 'value' }"
              @click="selectedField = 'value'">
              <i class="fa fa-coins" />
            </p>
            <p class="button is-medium is-rounded" :class="{ 'is-black': selectedField === 'updated' }"
              @click="selectedField = 'updated'">
              <i class="far fa-clock" />
            </p>
          </div>
        </slide-y-down-transition>

        <div class="bottom-bar">
          <div class="button" @click="clear">
            <i class="fa fa-chevron-left" />
          </div>
          <div class="button" @click="getMoreTransactions">
            <span v-if="loadMore"><i class="fa fa-atom fa-lg fa-spin" /></span>
            <span v-else><i class="fa fa-chevron-down" /></span>
          </div>
          <div class="button" @click="getRecentTransactions">
            <i class="fa fa-redo-alt" />
          </div>
          <div class="button" @click="toggleFields">
            <i class="fa fa-ellipsis-h" />
          </div>
        </div>
        <!-- </div> -->
      </div>
      <div v-else>
        <div class="workflow-types">
          <div class="is-pointer fleet-flex">
            <div v-for="f in deliveryFleets" :key="f.name" class="fleet" :class="fleetStateClass(f)"
              @click="selectFleet(f)">
              <span v-if="f.state === 'inactive' &&
                f.workflow_data.inactive_max_distance >= 1000
                " class="inactive-max-dist">
                {{ f.workflow_data.inactive_max_distance }}m
              </span>
              <span v-if="f.workflow_data.surge" class="inactive-max-dist surge-fee">
                +{{ f.workflow_data.surge }}
              </span>
              <div>
                {{ shortFleetName(f.name) }}
              </div>
              <span class="has-text-weight-bold">
                <span v-if="f.initiated_count" class="count has-text-danger bg-pulse" style="background-color: black">{{
                  f.initiated_count }}</span>&nbsp;
                <span class="count" :class="f.confirmed_count > 0 ? 'has-text-dark' : 'has-text-white'
                  ">{{ f.confirmed_count }}</span>
              </span>
            </div>
            <div v-if="!deliveryFleets" class="fleet" style="padding: 17px; background-color: #333">
              Loading...
            </div>
          </div>
          <div v-if="selectedFleet" class="selected-fleet-details notification">
            <button class="delete is-pulled-right is-medium" @click="selectedFleet = null" />
            <p class="label">
              <span class="link" @click="goto(`/business_profile/${selectedFleet.business_id}`)">{{ selectedFleet.name
              }}</span>
            </p>
            <p>
              <span class="button is-uppercase" :class="{
                  'has-text-danger has-text-weight-bold':
                    selectedFleet.state === 'inactive',
                }" @click="toggleFleetState(selectedFleet)">{{ selectedFleet.state }}</span>&nbsp;
              <span class="button" @click="editFleetDistance(selectedFleet)">{{
                selectedFleetDistance
              }}</span>
            </p>
            <p style="margin-bottom: 0">
              <span class="button" :class="selectedFleet.workflow_data.surge
                    ? 'has-text-danger has-text-weight-bold'
                    : 'has-text-grey'
                  " @click="goto(`/business_profile/${selectedFleet.business_id}`)">
                Surge: +{{ selectedFleet.workflow_data.surge || 0 }}
              </span>
            </p>
            <!-- <p class="buttons">
                  <span class="button" :class="{ 'is-success': true }">Normal</span>
                  <span class="button" :class="{ 'is-warning': true }"
                    >Degraded</span
                  >
                  <span class="button" :class="{ 'is-danger': true }">Off</span>
                </p>
                <div v-if="selectedFleet.workflow_data.profiles">
                  <p>Normal: {{ selectedFleet.workflow_data.profiles.normal }}</p>
                  <p>
                    Degraded: {{ selectedFleet.workflow_data.profiles.degraded }}
                  </p>
                  <p>Off: {{ selectedFleet.workflow_data.profiles.off }}</p>
                </div>
                <div v-else>No Profiles</div>
                <p
                  class="monospace"
                  style="font-size: 0.9em; color: blue; padding: 10px"
                >
                  {{ selectedFleet.workflow_data }}
                </p> -->
          </div>

          <div class="is-pointer notification is-dark fleet is-flex">
            <span @click="showPausedShops = !showPausedShops">
              <i class="far fa-hourglass fa-sm" />Paused
              <span v-if="pausedShops" class="is-pulled-right count has-text-light bg-black">
                {{ pausedShops.length }}
              </span>
            </span>
            <span @click="select('Remit')">
              <i class="fa fa-coins fa-sm" />Remit
            </span>
          </div>

          <p class="is-pointer notification is-dark is-flex" style="padding: 16px">
            <span @click="select('Private Tuk Tuk')">Private Tuk</span>
            <span @click="select('Motocy')">Motorbike</span>
            <span @click="select('Public Tuk Tuk')">Public Tuk</span>
          </p>

          <div class="is-pointer notification is-dark fleet has-text-left" @click="select('Commerce')">
            <i class="fa fa-store-alt fa-sm" />Commerce
            <span v-if="activeBasketsCount" class="has-text-info has-text-weight-medium count bg-black">{{
              activeBasketsCount }}</span>
            <span v-if="commerceOrderStats" class="is-pulled-right has-text-centered">
              <span class="count has-text-warning bg-black">{{
                commerceOrderStats.initiated || "&nbsp;"
              }}</span>&nbsp;
              <span class="count has-text-danger bg-black">{{
                commerceOrderStats.initiated_danger || "&nbsp;"
              }}</span>&nbsp;
              <span class="count has-text-danger bg-black" :class="{
                    'bg-pulse': commerceOrderStats.requires_confirmation,
                  }">{{
      commerceOrderStats.requires_confirmation || "&nbsp;"
    }}</span>
            </span>
          </div>
          <div class="is-pointer notification is-dark fleet has-text-left" @click="select('Delivery')">
            <i class="fa fa-motorcycle fa-sm" />Delivery
            <span v-if="activeDeliveryDrivers" class="is-pulled-right has-text-centered"
              @click.stop="toggleActiveDeliveryDrivers">
              <span class="count has-text-success bg-black">{{
                availableDeliveryDriversCount
              }}</span>&nbsp;
              <span class="count has-text-grey bg-black">{{
                busyDeliveryDriversCount
              }}</span>
            </span>
          </div>
        </div>
        <div class="stats-wrapper no-scrollbar" :class="{ 'stats-short': shortStats }">
          <div class="stats no-scrollbar">
            <div v-if="showActiveDeliveryDrivers">
              <p class="stats-header">
                Delivery Drivers
                <i class="delete is-large is-pulled-right" @click="showActiveDeliveryDrivers = false" />
              </p>
              <div v-if="!activeDeliveryDrivers">
                No Active Delivery Drivers
              </div>
              <div v-else>
                <p class="delivery-states">
                  <span>confirmed<br /><b>{{
                    deliveriesInState("confirmed") || "&nbsp;&nbsp;"
                  }}</b></span>
                  <span>arrived<br /><b>{{
                    deliveriesInState("driver_arrived") || "&nbsp;&nbsp;"
                  }}</b></span>
                  <span>enroute<br /><b>{{
                    deliveriesInState("enroute") || "&nbsp;&nbsp;"
                  }}</b></span>
                </p>
                <p v-for="d in availableDeliveryDrivers" :key="d.name" class="driver">
                  <span class="name">{{ shortenString(d.name, 20) }}</span>
                  <span class="state">--</span>
                  <!-- <span class="updated">{{ hhmmTime(d.updated_at) }}</span> -->
                  <span class="updated"><i class="far fa-clock fa-sm" style="margin-right: 3px" />{{
                    driverLastUpdated(d.updated_at) }}</span>
                </p>
                <p v-for="d in busyDeliveryDrivers" :key="d.name" class="driver">
                  <span class="name">{{ shortenString(d.name, 20) }}</span>
                  <span class="state">{{ removeStates(d.states) }}</span>
                  <!-- <span class="updated">{{ hhmmTime(d.updated_at) }}</span> -->
                  <span class="updated"><i class="far fa-clock fa-sm" style="margin-right: 3px" />{{
                    driverLastUpdated(d.updated_at) }}</span>
                </p>
              </div>
              <p class="has-text-right" @click="showActiveDeliveryDrivers = false">
                <!-- <i class="fa fa-chevron-down" style="padding-right: 16px" /> -->
              </p>
            </div>
            <div v-else-if="showPausedShops">
              <p class="stats-header">
                Paused Shops
                <i class="delete is-large is-pulled-right" @click="showPausedShops = false" />
              </p>
              <p class="paused-shops">
                <span v-if="!pausedShops || !pausedShops.length">No Paused Shops</span>
                <span v-for="s in pausedShops" :key="s.name" class="shop-line" @click="unpauseShop(s)">
                  <span class="shop">{{ shortenString(s.name, 30) }}</span>
                  <span class="is-pulled-right monospace">{{
                    hhmmTime(s.updated_at)
                  }}</span>
                </span>
              </p>
              <p class="has-text-right" @click="showPausedShops = false">
                <!-- <i class="fa fa-chevron-down" style="padding-right: 16px" /> -->
              </p>
            </div>
            <div v-else>
              <p v-if="longRunningQueries" class="long-running-warning has-text-danger">
                {{ longRunningQueries }} Long-Running Queries
              </p>
              <p class="stats-header has-text-warning">
                Stats
                <span style="padding: 6px; color: #777" @click="killLongRunning">
                  <i class="fa fa-bomb" />

                </span>
                <span v-if="statsUpdated" class="stats-updated">{{
                  statsUpdated.toLocaleTimeString()
                }}</span>
              </p>
              <div class="table" @click="getStats">
                <tr v-for="(v, k) in stats" :key="k" style="margin-bottom: 6px">
                  <td class="is-capitalized">
                    {{ k.replace(/_/g, " ").split(/ (.+)/)[1] }}
                  </td>
                  <td class="has-text-weight-medium has-text-info">{{ v }}</td>
                </tr>
              </div>
              <p v-if="!stats" style="opacity: 0.3">
                <b-skeleton height="20px" />
                <b-skeleton height="20px" />
                <b-skeleton height="20px" />
                <b-skeleton height="20px" />
                <b-skeleton height="20px" />
              </p>
            </div>
          </div>
          <div class="bottom-btns">
            <p @click="goto('driver')">
              <i class="fa fa-motorcycle fa-lg" />
            </p>
            <p @click="goto('ecommerce')">
              <i class="fa fa-list fa-lg" />
            </p>
            <p @click="gotoUserLookup">
              <i class="far fa-user fa-lg" />
            </p>
          </div>
        </div>
      </div>
    </div>

    <WorkflowTransactionDetails v-if="selectedTransaction" :t="selectedTransaction" @close="selectedTransaction = null"
      @update="getRecentTransactions" />
  </div>
</template>

<script>
import { sounds } from "../functions.js";
import WorkflowTransactionDetails from "@/components/WorkflowTransactionDetails.vue";

export default {
  name: "SuperOps",
  components: {
    WorkflowTransactionDetails,
  },
  props: {
    showChevron: { type: Boolean, default: true },
  },
  data() {
    return {
      selectedField: null,
      activeBasketsCount: null,
      interval: null,
      polling: false,
      selectedFleet: null,
      shortStats: false,
      showActiveDeliveryDrivers: false,
      activeDeliveryDrivers: null,
      commerceOrderStats: null,
      showPausedShops: false,
      pausedShops: null,
      deliveryFleets: null,
      showAllFields: false,
      statusFilter: "ongoing",
      searchInput: null,
      searchTerm: null,
      showSearch: false,
      stats: null,
      statsUpdated: null,
      longRunningQueries: null,
      transactions: [],
      workflow: null,
      selected: null, // Delivery, Sii Lor, Commerce etc
      updating: false,
      workflowTypes: [
        "Private Tuk Tuk",
        // "Sii Lor",
        "Public Tuk Tuk",
        "Motocy", // motocy chiang mai
        // "Fleet", // << motocy bangkok, any new transport
        "Delivery",
        "Commerce",
      ],
      selectedTransaction: null,
      loadMore: false,
      showBizName: false,
      fieldIdx: 0,
      fields: ["shop", "customer", "driver", "value", "updated"],
      remitStates: ["initiated", "ready", "pending", "completed"],
      commerceStates: ["initiated", "confirmed", "ready", "completed"],
      deliveryStates: [
        "initiated",
        "confirmed",
        "driver_arrived",
        "enroute",
        "alighted",
      ],
    };
  },
  computed: {
    selectedFleetDistance() {
      if (this.selectedFleet.state === "inactive") {
        return this.selectedFleet.workflow_data.inactive_max_distance + "m";
      }
      if (this.selectedFleet.workflow_data.pricing_array) {
        return this.selectedFleet.workflow_data.pricing_array.length - 1 + "km";
      }
      return "?m";
    },
    relevantStates() {
      if (this.selected === "Commerce") return this.commerceStates;
      if (this.selected === "Remit") return this.remitStates;
      return this.deliveryStates;
    },
    availableDeliveryDrivers() {
      if (!this.activeDeliveryDrivers) return [];
      var busyStates = ["initiated", "confirmed", "driver_arrived", "enroute"];
      return this.activeDeliveryDrivers.filter((d) => {
        var available = true;
        busyStates.forEach((s) => {
          if (d.states.includes(s)) available = false;
        });
        return available;
      });
    },
    busyDeliveryDrivers() {
      if (!this.activeDeliveryDrivers) return [];
      var busyStates = ["initiated", "confirmed", "driver_arrived", "enroute"];
      return this.activeDeliveryDrivers.filter((d) => {
        var busy = false;
        busyStates.forEach((s) => {
          if (d.states.includes(s)) busy = true;
        });
        return busy;
      });
    },
    availableDeliveryDriversCount() {
      return this.activeDeliveryDrivers.length - this.busyDeliveryDriversCount;
    },
    busyDeliveryDriversCount() {
      if (!this.activeDeliveryDrivers) return 0;
      var busyStates = ["initiated", "confirmed", "driver_arrived", "enroute"];
      return this.activeDeliveryDrivers.filter((d) => {
        var busy = false;
        busyStates.forEach((s) => {
          if (d.states.includes(s)) busy = true;
        });
        return busy;
      }).length;
    },
    field() {
      if (this.selectedField) return this.selectedField;
      return this.fields[this.fieldIdx];
    },
    fieldColor() {
      if (this.field === "shop") return "has-text-warning";
      if (this.field === "driver") return "has-text-info";
      if (this.field === "value") return "has-text-success";
      if (this.field === "updated") return "has-text-danger";
      return null;
    },
    fieldIcon() {
      if (this.field === "shop") return "fa-store-alt";
      if (this.field === "driver" && this.selected === "Commerce") {
        return "fa-headset";
      }
      if (this.field === "driver") return "fa-motorcycle";
      if (this.field === "customer") return "fa-user";
      if (this.field === "value") return "fa-coins";
      if (this.field === "updated") return "fa-clock";
      return "fa-kiwi-bird";
    },
    filteredTransactions() {
      if (!this.searchTerm) return this.transactions;
      return this.transactions.filter((t) => {
        return JSON.stringify(t).toLowerCase().includes(this.searchTerm);
      });
    },
    matchingFields() {
      if (!this.searchTerm) return null;
      var fields = {};
      this.transactions.filter((t) => {
        Object.keys(t).forEach((key) => {
          if (JSON.stringify(t[key]).toLowerCase().includes(this.searchTerm)) {
            // increment
            if (fields[key]) fields[key] += 1;
            else fields[key] = 1;
          }
        });
      });
      return fields;
    },
  },
  created() {
    this.$root.$refs.SuperRecentTransactions = this; // used to catch back button on home page
  },
  beforeDestroy() {
    this.$root.$refs.SuperRecentTransactions = null; // clear it
    document.removeEventListener("visibilitychange", this.reinit);
  },
  deactivated() {
    clearInterval(this.interval);
  },
  activated() {
    this.interval = setInterval(() => {
      if (this.polling) {
        this.getStats();
        this.updateDeliveryFleets();
        this.updateActiveDeliveryDrivers();
        this.getCommerceOrderStats();
        this.getActiveBasketsCount();
      }
    }, 1000 * 30);
  },
  mounted() {
    document.addEventListener("visibilitychange", this.reinit);
    if (!this.isSuperUser()) this.goHome();
    this.getStats();
    this.updateDeliveryFleets();
    this.updateActiveDeliveryDrivers();
    this.getCommerceOrderStats();
    this.getActiveBasketsCount();
    this.updatePausedShops();
    setTimeout(() => (this.polling = true), 2000);
    this.selectedField = this.fields[this.fieldIdx];
  },
  methods: {
    reinit() {
      if (document.visibilityState === "visible") this.getStats(); // update on visible
    },
    gotoUserLookup() {
      this.goto("/user_lookup");
    },
    leftIcon(t) {
      if (this.isDelivery(t) || this.isTransport(t)) return false;
      return true;
    },
    isAutoconfirm(t) {
      return t.data.autoconfirm && !t.data.event_preconfirm;
    },
    selectFleet(f) {
      if (f === this.selectedFleet) this.selectedFleet = null;
      else this.selectedFleet = f;
      // var loader = this.loadingDots();
      // setTimeout(() => loader.hide(), 150);
    },
    startPolling() {
      this.polling = true;
    },
    stopPolling() {
      this.polling = false;
    },
    pulseTransaction(t) {
      return (
        this.awaitingAutoconfirm(t) || this.deliveryRequiresConfirmation(t)
      );
    },
    isCommerceDelayDelivery(t) {
      if (this.selected !== "Commerce") return false;
      if (t.state !== "confirmed") return false;
      if (!t.data.delivery) return false;
      return t.data.delivery.delay_duration;
    },
    deliveryRequiresConfirmation(t) {
      if (this.selected !== "Delivery") return false;
      if (!t.data.event_assigned) return false;
      return !t.data.acknowledged;
    },
    isFirstOrder(t) {
      if (this.selected !== "Commerce") return false;
      if (!t.data.ref) return false;
      return t.data.ref.toString().endsWith("01");
    },
    unpauseShop(s) {
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.3em; margin-bottom:12px'><i class='fa fa-play'></i>&nbsp;Unpause Shop?</p>
        <p><b>${s.name}</b></p>
        </div>`,
        showCloseButton: true,
        confirmButtonColor: this.bulmaColor("success"),
        confirmButtonText: "Un-Pause",
      }).then((val) => {
        if (val.value) this.infoToast("TODO");
      });
    },
    driverLastUpdated(dt) {
      var minsAgo = (new Date() - new Date(dt)) / (1000 * 60);
      return Math.round(minsAgo);
    },
    toggleActiveDeliveryDrivers() {
      this.showActiveDeliveryDrivers = !this.showActiveDeliveryDrivers;
      // this.showPausedShops = true;
    },
    deliveriesInState(state) {
      var count = 0;
      this.activeDeliveryDrivers.forEach((d) => {
        d.states.split(",").forEach((s) => {
          if (s === state) count += 1;
        });
      });
      return count;
    },
    removeStates(states) {
      return states
        .split(",")
        .filter((s) => s !== "alighted" && s !== "cancelled_by_passenger")
        .join(",");
    },
    fleetStateClass(f) {
      var selected = null;
      if (f === this.selectedFleet) selected = "selected-fleet";
      if (f.state === "inactive") {
        if (f.workflow_data.inactive_max_distance >= 1000) {
          return ["bg-yellow", selected];
        }
        return ["bg-red", selected];
      }
      return ["bg-green", selected];
    },
    shortState(state) {
      // shorten: cancelled_by... and driver_arrived
      return state
        .replace("cancelled", "x")
        .replace("driver_", "d_")
        .replace("passenger", "pax");
    },
    awaitingAutoconfirm(t) {
      return t.data.autoconfirm && !t.driver;
    },
    workflowIcon(name) {
      if (name === "Motocy") return "fa-motorcycle";
      if (name === "Private Tuk Tuk") return "fa-car";
      if (name === "Public Tuk Tuk") return "fa-car";
      if (name === "Delivery") return "fa-box";
      if (name === "Commerce") return "fa-store-alt";
      return "fa fa-question";
    },
    deliveryIssue(t) {
      if (t.workflow_type_name !== "Delivery") return false;
      if (!t.is_ongoing) return false;
      if (t.state === "alighted") return false;
      var diff = new Date() - new Date(t.updated_at);
      var minsAgo = diff / (1000 * 60);
      return minsAgo >= 20;
    },
    toggleStatusFilter() {
      if (this.statusFilter === "all") this.statusFilter = "ongoing";
      else this.statusFilter = "all";
      this.getRecentTransactions();
    },
    setStatusFilter(filter) {
      this.statusFilter = filter;
      this.showSearch = false;
      this.getRecentTransactions();
    },
    gotoIssues() {
      // this.goto('issues')
      // "http://redash.tukbot.com/queries/544#890"
      var url = `http://redash.tukbot.com/dashboard/delivery-issues-today?refresh=45`;
      window.open(url, "_blank");
    },
    handleBackButton() {
      console.log("handleBackButton");
      if (this.selectedTransaction) this.selectedTransaction = null;
      else if (this.selected) this.selected = null;
      else this.$emit("close");
    },
    isTest(t) {
      return JSON.stringify(t.data).includes("TEST");
    },
    toggleSearch() {
      if (this.showSearch) {
        this.showSearch = false;
      } else {
        this.showSearch = true;
        // this.$nextTick(() => this.$refs.input.focus());
      }
    },
    clearSearch() {
      this.showSearch = false;
      this.searchInput = null;
      this.searchTerm = null;
    },
    search(e) {
      var text = e.target.value; //   var text = this.searchVal;
      this.searchTerm = text.toLowerCase(); // a hack for mobile...
    },
    isLalamove(t) {
      return t.data.lalamove_order_id;
    },
    isContactless(t) {
      if (!t.data.delivery) return null;
      return t.data.delivery.contactless_note;
    },
    updatePausedShops() {
      this.pausedShops = null;
      this.$store
        .dispatch("getPausedShops")
        .then((res) => {
          if (res.data) this.pausedShops = res.data;
          else this.pausedShops = [];
        })
        .catch((err) => this.apiError(err));
    },
    updateDeliveryFleets() {
      this.selectedFleet = null;
      this.deliveryFleets = null;
      this.$store
        .dispatch("getDeliveryFleetStats")
        .then((res) => {
          this.deliveryFleets = res.data;
        })
        .catch((err) => this.apiError(err));
    },
    updateActiveDeliveryDrivers() {
      this.activeDeliveryDrivers = null;
      this.$store
        .dispatch("getActiveDeliveryDrivers")
        .then((res) => {
          this.activeDeliveryDrivers = res.data;
        })
        .catch((err) => this.apiError(err));
    },
    getActiveBasketsCount() {
      this.activeBasketsCount = 0;
      this.$store
        .dispatch("getActiveBasketsCount")
        .then((res) => {
          this.activeBasketsCount = res.data;
        })
        .catch((err) => this.apiError(err));
    },
    getCommerceOrderStats() {
      this.commerceOrderStats = null;
      this.$store
        .dispatch("getCommerceOrderStats")
        .then((res) => {
          this.commerceOrderStats = res.data;
        })
        .catch((err) => this.apiError(err));
    },
    getStats() {
      this.updateDeliveryFleets();
      this.updateActiveDeliveryDrivers();
      this.updatePausedShops();
      this.getCommerceOrderStats();
      this.stats = null;
      this.$store
        .dispatch("getSuperuserStats")
        .then((res) => {
          this.statsUpdated = new Date(res.data.updated_at);
          this.longRunningQueries = res.data.long_running_queries_count;
          this.stats = res.data;
          delete this.stats.updated_at;
          delete this.stats.long_running_queries_count;
        })
        .catch((err) => this.apiError(err));
    },
    killLongRunning() {
      var loader = this.loadingDots("pink");
      this.$store
        .dispatch("killLongRunningQueries")
        .then((res) => {
          console.log("killLongRunningQueries", res.data);
          this.successToast(res.data);
        })
        .catch((err) => this.apiError(err))
        .finally(() => loader.hide());
    },
    // modified from shortDateTime in main.js
    shorterDateTime(timeString) {
      var dt = new Date(timeString);
      var date = dt.toLocaleString("en-GB").slice(0, 5);
      var time = dt.toLocaleTimeString("en-GB").slice(0, 5);
      if (this.ageHours(dt) < 24) return time;
      return `${date}, ${time}`;
    },
    ageHours(dt) {
      return (new Date().getTime() - dt.getTime()) / 1000 / (60 * 60);
    },
    nextField() {
      this.selectedField = null;
      this.fieldIdx += 1;
      if (this.fieldIdx === this.fields.length) this.fieldIdx = 0;
    },
    toggleFields() {
      this.showAllFields = !this.showAllFields;
    },
    toggleBizName() {
      this.showBizName = !this.showBizName;
    },
    isScheduled(t) {
      if (!t.data.order) return;
      return t.data.order.fulfilment_time;
    },
    isDelayedDelivery(t) {
      if (!t.data.delivery) return;
      return t.data.delivery.delay_duration;
    },
    isCashback(t) {
      if (!this.isDelivery(t)) return null;
      return t.data.delivery.cashback;
    },
    isDelivery(t) {
      return t.data.type === "delivery";
    },
    isTransport(t) {
      var notTransport = ["Commerce", "Delivery"];
      return !notTransport.includes(t.workflow_type_name);
    },
    isTakeaway(t) {
      return t.data.type === "take-away";
    },
    isDinein(t) {
      return t.data.type === "dine-in";
    },
    clear() {
      if (this.selected) {
        this.selected = null;
        this.reinit();
      } else {
        this.goHome();
      }
    },
    select(workflowType) {
      this.selected = workflowType;
      this.getRecentTransactions();
    },
    currency(t) {
      var country = "th"; // default country
      if (t.data.business) country = t.data.business.country;
      return this.currencySymbol(country); // should store in order
    },
    customerName(t) {
      if (this.field === "shop") {
        if (this.selected === "Commerce") return t.data.business.name;
        if (this.selected === "Delivery") return t.data.delivery.business_name;
      }
      if (this.field === "driver") {
        if (this.selected === "Commerce" && !this.isDelivery(t)) {
          return "- Not Delivery -";
        }
        if (this.selected === "Commerce" && !t.driver) {
          return "- No Agent Yet -";
        }
        // if (this.selected === "Delivery") {
        if (!t.driver) return "- No Driver Yet -";
        return t.driver.name;
        // }
      }
      if (this.field == "value") {
        if (this.selected === "Commerce") {
          var billingRemit = "";

          if (t.data.order.remit)
            billingRemit += `<span style='font-size:0.8em'><i class='fa fa-store-alt fa-sm has-text-white' style='margin:0 6px'></i>${t.data.order.remit || 0
              }</span>&nbsp;`;
          if (t.data.billing)
            billingRemit += `<span style='font-size:0.8em'><i class='fa fa-file-invoice fa-sm has-text-warning' style='margin:0 6px'></i>${t.data.billing || 0
              }</span>`;
          return this.currency(t) + t.data.order.order_value + billingRemit;
        }
        if (this.selected === "Delivery") {
          var orderValue = ` + ${t.data.delivery.order_value}`;
          return this.currency(t) + t.data.fare.price + orderValue;
        }
        // transport or other?
        return this.currency(t) + t.data.fare.price;
      }
      if (this.field == "updated") {
        return this.timeAgo(t.updated_at);
      }
      return t.customer.name || t.customer.phone_number;
    },
    fieldValue(t) {
      if (this.selected === "Commerce") {
        return this.currency(t) + t.data.order.order_value;
      }
      if (this.selected === "Delivery") {
        var orderValue = ` + ${t.data.delivery.order_value}`;
        return this.currency(t) + t.data.fare.price + orderValue;
      }
      // transport or other?
      if (!t.data.fare) return "Free";
      return this.currency(t) + t.data.fare.price;
    },
    fieldBusiness(t) {
      if (!t.data || (this.selected === "Commerce" && !t.data.business))
        return "ERR";
      if (this.selected === "Commerce") return t.data.business.name;
      if (this.selected === "Delivery") return t.data.delivery.business_name;
      return "Business Name";
    },
    fieldDriverAgent(t) {
      if (this.selected === "Commerce" && !this.isDelivery(t)) {
        return "- Not Delivery -";
      }
      if (this.selected === "Commerce" && !t.driver) {
        return "- No Agent Yet -";
      }
      // if (this.selected === "Delivery") {
      if (!t.driver) return "- No Driver Yet -";
      return t.driver.name;
    },
    shortTime(t) {
      var str = new Date(t.created_at).toLocaleTimeString("en-GB");
      return str.slice(0, 5);
    },
    getRecentTransactions() {
      var payload = {
        type: this.selected,
        offset: 0,
        status: this.statusFilter,
      };
      this.updating = true;
      this.$store
        .dispatch("getTransactionsByWorkflowType", payload)
        .then((res) => (this.transactions = res.data || []))
        .catch((err) => this.apiError(err))
        .finally(() => (this.updating = false));
    },
    getMoreTransactions() {
      var payload = {
        type: this.selected,
        offset: this.transactions.length,
        status: this.statusFilter,
      };
      this.loadMore = true;
      this.$store
        .dispatch("getTransactionsByWorkflowType", payload)
        .then((res) => {
          console.log("getMoreTransactions", res);
          if (res.data) this.transactions = this.transactions.concat(res.data);
          else this.infoToast("No more transactions");
        })
        .catch((err) => this.apiError(err))
        .finally(() => (this.loadMore = false));
    },
    date(t) {
      return new Date(t.created_at).getDate();
    },
    transactionStyle(t) {
      // style means background-color of tag, which should depend on transaction type
      var color = "#ff85A8"; //"pink";
      if (t.state === "scheduled") color = "gold";
      var successStates = ["alighted", "completed"];
      // if (successStates.includes(t.state)) color = "palegreen";
      if (successStates.includes(t.state)) color = "#bbb";
      // var liveStates = [
      //   "initiated",
      //   "confirmed",
      //   "driver_arrived",
      //   "enroute",
      //   "ready",
      // ];
      // if (liveStates.includes(t.state)) color = "#0dd";
      if (t.state === "initiated") color = "yellow";
      if (t.state === "confirmed") color = "orange";
      if (t.state === "driver_arrived") color = "#0dd";
      if (t.state === "enroute") color = "palegreen";
      if (t.state === "ready") color = "palegreen";
      if (t.workflow_type_name === 'Remit') {
        if (t.state === "ready") color = "#ff85A8"; // pink
      }
      if (t.state === "pending") color = "palegreen";
      return `background-color: ${color}`; // failed states: timed_out, cancelled, no_show
    },
    moreInfo(t) {
      this.selectedTransaction = t;
    },
    retryDelivery(t) {
      sounds("mystery_long");
      var loader = this.loadingDots();
      this.$store
        .dispatch("patchTransaction", {
          transactionID: t.id,
          data: { state: "initiated", data: { retry: true } },
        })
        .then(() => {
          this.infoToast("We are retrying the delivery request 🤞🏽");
          this.getRecentTransactions();
        })
        .catch((err) => this.apiError(err))
        .finally(() => loader.hide());
    },
    shortFleetName(name) {
      return name
        .replace("Express Delivery", "")
        .replace("Chiang Mai", "CM")
        .replace("Bangkok", "BKK");
    },
    editFleetDistance(f) {
      if (f.state !== "inactive") {
        var msg = "To change the fleet distance<br>First set it to INACTIVE";
        this.errorToast(msg, "top");
        return;
      }
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='margin-bottom:12px; font-size:1.2em'>
        <i class='fa fa-route'></i>&nbsp;Edit fleet distance?</p>
        <p style='margin-bottom:10px'><b>${f.name}</b></p>
        <p>Current inactive_max_distance: <b>${f.workflow_data.inactive_max_distance}m</b></p>
        </div>`,
        input: "number",
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: "Submit",
        cancelButtonText: "Zero",
        cancelButtonColor: this.bulmaColor("danger"),
        confirmButtonColor: this.bulmaColor("info"),
        inputValidator: (val) => {
          if (!val || val < 1000)
            return "Enter a distance >= 1000m<br>Or choose ZERO";
        },
      }).then((val) => {
        if (val.dismiss === "close" || val.dismiss === "backdrop") {
          return;
        }
        var newDistance = 0;
        if (val.dismiss !== "cancel") {
          newDistance = parseInt(val.value);
        }
        var loader = this.loadingDots("yellow");
        this.$store
          .dispatch("updateWorkflowData", {
            id: f.workflow_id,
            data: { inactive_max_distance: newDistance },
          })
          .then(() => {
            this.updateDeliveryFleets();
            this.successToast("Fleet state updated!");
            this.selectedFleet = null;
          })
          .catch((err) => this.apiError(err))
          .finally(() => loader.hide());
      });
    },
    toggleFleetState(f) {
      var newState = "active";
      var buttonText = "UN-PAUSE";
      var buttonColor = this.bulmaColor("success");
      if (f.state === "active") {
        newState = "inactive";
        buttonText = "PAUSE";
        buttonColor = this.bulmaColor("danger");
      }
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='margin-bottom:12px; font-size:1.2em'>
        <i class='fa fa-motorcycle'></i>&nbsp;Toggle fleet?</p>
        <p style='margin-bottom:10px'><b>${f.name}</b></p>
        <p>Current state: <b>${f.state}</b></p>
        </div>`,
        showCloseButton: true,
        // showCancelButton: true,
        confirmButtonText: buttonText, //this.$t("yes"),
        confirmButtonColor: buttonColor,
        // cancelButtonText: this.$t("no"),
      }).then((val) => {
        if (val.value) {
          var loader = this.loadingDots("orange");
          this.$store
            .dispatch("setWorkflowState", {
              id: f.workflow_id,
              state: newState,
            })
            .then(() => {
              this.updateDeliveryFleets();
              this.successToast("Fleet state updated!");
              this.selectedFleet = null;
            })
            .catch((err) => this.apiError(err))
            .finally(() => loader.hide());
        }
      });
    },
  },
};
</script>

<style scoped>
.wrapper {
  position: absolute;
  top: 0;
  /* height: 100vh; */
  height: 100%;
  width: 100vw;
  overflow: hidden;
  background-color: #0b161e;
  /* z-index: 5; */
  color: white;
  /* backdrop-filter: blur(5px); */
}

/* .pattern {
  background-image: url("/img/home2/home-dots-pattern.png");
  background-size: contain;
  background-position: center;
} */
.header {
  padding: 16px;
  text-align: left;
  font-size: 1.3em;
  font-weight: 450;
  margin-bottom: 0;
  color: white;
}

.delete {
  position: absolute;
  right: 12px;
  top: 12px;
}

.panel-block {
  font-size: 1.15em;
  white-space: nowrap;
  /* overflow: auto; */
  overflow: hidden;
  border-radius: 3px;
  padding: 6px;
  color: grey;
  border: 1px solid rgba(192, 203, 111, 0.1);
  margin-bottom: -1px;
  /* border: 1px solid grey; */
  background-color: rgba(69, 69, 69, 0.2);
  color: rgba(255, 255, 255, 0.8);
}

.panel-block:hover {
  padding: 10px;
}

.transaction {
  text-align: left;
  font-size: 1.15em;
  white-space: nowrap;
  /* overflow: auto; */
  overflow: hidden;
  border-radius: 3px;
  padding: 6px;
  color: grey;
  border: 1px solid rgba(192, 203, 111, 0.1);
  margin-bottom: -1px;
  /* border: 1px solid grey; */
  background-color: rgba(69, 69, 69, 0.2);
  color: rgba(255, 255, 255, 0.8);
}

.border-big {
  border-top: 8px solid #333;
}

.transaction:hover {
  outline: 1px solid gold;
  box-shadow: 0 0 2px gold;
  /* border: 1px solid gold; */
}

.time {
  font-size: 0.8em;
  font-weight: 500;
  font-family: Lucida Console, monospace;
}

.tag {
  font-size: 0.7em;
  padding: 5px;
}

.tag .panel-block::-webkit-scrollbar {
  display: none;
}

.workflow-types p {
  margin-bottom: 12px;
}

p .tag {
  width: 220px;
  font-weight: 500;
}

.refresh {
  padding: 10px;
  background-color: rgba(128, 128, 128, 0.2);
  color: white;
}

.deleted {
  background-color: rgba(254, 89, 130, 0.4);
}

.notification {
  padding: 10px;
  opacity: 0.8;
  margin-top: 1px;
  border-radius: 0;
}

.notification.is-pointer {
  padding: 16px;
}

.stats-wrapper {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100vw;
  background-color: black;
  color: grey;
  padding-top: 12px;
  /* padding-bottom: 12px; */
  /* margin: 10px; */
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  box-shadow: 0 0 4px 2px white;
}

.stats-short {
  max-height: 200px;
  overflow: auto;
}

.stats-header {
  font-size: 1.2em;
  text-align: left;
  margin-bottom: 10px;
  margin-left: 6px;
}

.long-running-warning {
  font-size: 0.6em;
  position: absolute;
  top: 3px;
  left: 16px;
  font-weight: 550;
}

.stats {
  padding: 0 10px;
  padding-bottom: 20px;
  max-height: 50vh;
  overflow: auto;
}

.stats-updated {
  float: right;
  font-weight: 500;
  color: rgba(255, 217, 0, 0.4);
  margin-right: 12px;
}

.table {
  background-color: initial;
  color: #999;
}

.transactions-list {
  max-height: calc(100vh - 60px);
  overflow: auto;
  padding-bottom: 180px;
}

/* .search {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: 14px 10px;
  z-index: 2;
  background-color: rgba(0, 0, 0, 0.418);
} */
.input {
  background-color: lightgrey;
  color: black;
}

.input:placeholder {
  color: lightgrey;
}

.search.button {
  background-color: grey;
  color: white;
}

.search-fields {
  /* margin-top: -8px;
  margin-bottom: -10px; */
  margin-bottom: 0;
}

.extra-margin {
  margin-top: 30px;
}

.bg-test {
  background-color: #2f2d21;
  opacity: 1;
  background-image: repeating-linear-gradient(45deg,
      #131312 25%,
      transparent 25%,
      transparent 75%,
      #131312 75%,
      #131312),
    repeating-linear-gradient(45deg,
      #131312 25%,
      #2f2d21 25%,
      #2f2d21 75%,
      #131312 75%,
      #131312);
  background-position: 0 0, 6px 6px;
  background-size: 12px 12px;
}

.border-pulse {
  animation: pulsate-2 2s ease-out infinite;
}

@keyframes pulsate-2 {
  0% {
    box-shadow: 0 0 0 1px cyan;
  }

  50% {
    box-shadow: 0 0 0 4px blue;
  }

  100% {
    box-shadow: 0 0 0 1px cyan;
  }
}

.bg-pulse {
  animation: pulsate-1 3s ease-out infinite;
}

@keyframes pulsate-1 {
  0% {
    box-shadow: 0 0 0 1px inset cyan;
  }

  50% {
    box-shadow: 0 0 0 6px inset rgba(255, 217, 0, 0.5);
  }

  100% {
    box-shadow: 0 0 0 1px inset lightskyblue;
  }
}

td {
  padding: 3px 12px;
  border-bottom: initial;
}

.search {
  position: absolute;
  bottom: 80px;
  left: 0;
  width: 100%;
  /* padding: 14px 10px; */
  padding: 10px;
  z-index: 2;
  background-color: #444;
}

.search .tags {
  font-size: 1.4em;
}

.search-btn {
  position: absolute;
  bottom: 80px;
  left: 0;
  margin: 10px;
  padding: 12px;
  opacity: 0.6;
}

.clear-btns {
  position: absolute;
  bottom: 80px;
  left: 0;
  /* margin: 10px; */
  padding: 10px;
  /* opacity: 0.6; */
  /* width: calc(100% - 20px); */
  width: 100%;
  display: flex;
  gap: 4px;
  background-color: #444;
}

.clear-btns p {
  flex: 1;
}

.clear-btns .button {
  padding: 4px;
}

.bottom-bar {
  position: absolute;
  bottom: 0;
  width: 100vw;
  display: flex;
}

.bottom-bar .button {
  flex: 1;
  border-radius: 0;
  font-size: 1.3em;
  background-color: #444;
  border: 1px solid #666;
  color: #aaa;
  height: 80px;
}

.bottom-btns {
  /* margin-top: 10px; */
  padding: 16px;
  display: flex;
  background-color: #444;
  color: white;
}

.bottom-btns p {
  flex: 1;
}

.status-filter {
  margin-left: 6px;
  font-size: 0.8em;
  font-weight: 450;
  padding: 0px 3px;
  background-color: black;
  border-radius: 3px;
}

.workflow-icon {
  position: absolute;
  left: 16px;
  padding: 4px 0;
}

.status {
  display: inline-block;
  width: 150px;
  margin: 0 6px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: 400;
}

.left-icon {
  font-size: 0.8em;
  background-color: white;
  border-radius: 50%;
  color: black;
  padding: 0 4px;
  margin-left: 3px;
}

.right-icons {
  margin-top: 3px;
  float: right;
  font-size: 0.8em;
  /* position: absolute; */
  /* margin-right: 2px; */
}

.right-icons i {
  margin-left: 3px;
  width: initial;
}

.all-fields {
  font-size: 0.8em;
}

.fleet {
  position: relative;
  margin-bottom: 12px;
}

.fleet-flex {
  display: flex;
  margin-bottom: 12px;
}

.fleet-flex .fleet {
  flex: 1;
  margin: 2px;
  padding: 2px;
}

.fleet .tag {
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.fleet .fa,
.far {
  width: 30px;
}

.fleet .count {
  border: 1px solid rgba(255, 255, 255, 0.3);
  background-color: white;
  border-radius: 2px;
  width: 30px;
  display: inline-block;
  margin-bottom: 3px;
}

.count {
  text-align: center;
  font-weight: 500;
}

.is-flex {
  margin-top: 6px;
  padding-top: 4px;
  /* border-radius: 4px; */
  /* border-top: 1px solid #333; */
}

.is-flex span {
  flex: 1;
}

.bg-red {
  background-color: var(--danger);
  border-radius: 3px;
}

.bg-yellow {
  color: black;
  background-color: var(--warning);
  border-radius: 3px;
}

.bg-green {
  background-color: var(--success);
  border-radius: 3px;
}

.paused-shops {
  text-align: left;
  padding: 10px;
  max-height: calc(40vh);
  overflow: auto;
  /* background-color: #333; */
  /* padding-bottom: 30px; */
}

.paused-shops .shop {
  white-space: nowrap;
  background-color: #222;
  border-radius: 2px;
  padding: 0 2px;
  margin-right: 3px;
  color: white;
}

.shop-line {
  display: block;
}

.active-drivers {
  /* text-align: left; */
  padding: 0 10px;
  max-height: calc(40vh);
  overflow: auto;
  /* background-color: #333; */
}

.driver {
  display: flex;
  margin-bottom: 4px !important;
}

.driver .name {
  white-space: nowrap;
  /* background-color: #555; */
  background-color: #444;
  color: white;
  border-radius: 2px;
  padding: 0 4px;
  /* margin-right: 3px; */
  flex-shrink: 1;
}

.driver .updated {
  flex-shrink: 1;
  text-align: right;
  /* background-color: black; */
  padding: 0 2px;
  border-radius: 2px;
  margin-left: 5px;
  font-family: Lucida Console, monospace;
}

.driver .state {
  flex: 1;
  text-align: right;
}

.bg-black {
  background-color: black !important;
}

.close-stats {
  position: fixed;
  bottom: 0;
  right: 0;
  padding: 10px;
  margin-right: 10px;
  color: white;
}

.delivery-states {
  display: flex;
}

.delivery-states span {
  flex: 1;
  /* background-color: black; */
  background-color: #333;
  margin: 2px;
  /* justify-content: space-between; */
  margin-bottom: 10px;
  padding-bottom: 2px;
  border-radius: 2px;
  text-align: center;
  color: lightgoldenrodyellow;
}

span>b {
  background-color: #444;
  padding: 0 3px;
  border-radius: 9px;
}

.retry-count {
  margin-left: 3px;
  margin-right: -4px;
  font-weight: 500;
  font-size: 1.1em;
}

.is-test {
  position: absolute;
  right: 7px;
  padding: 0 3px;
  background-color: red;
  border-radius: 2px;
}

.selected-fleet-state {
  background-color: #ddd;
  padding: 2px 4px;
  font-size: 0.8em;
  margin-left: 8px;
  border-radius: 3px;
}

.showing-toggle {
  /* background-color: rgba(0, 0, 0, 0); */
  border-top: 10px solid #444;
  padding: 18px;
  text-align: left;
}

.user-lookup {
  position: absolute;
  right: 0;
  bottom: 20px;
  padding: 16px;
}

.selected-fleet {
  border: 2px solid white;
}

.selected-fleet-details {
  margin: -13px 2px 12px 2px;
  border-radius: 3px;
  text-align: left;
}

.inactive-max-dist {
  position: absolute;
  font-size: 0.75em;
  font-weight: 500;
  margin-top: -7px;
  left: 5px;
  line-height: 1em;
  background-color: rgba(255, 255, 255, 1);
  border-radius: 2px;
  box-shadow: 0 0 2px black;
  padding: 1px;
}

.surge-fee {
  left: initial;
  right: 5px;
  color: red;
}
</style>